import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { ShortUrl } from '@air/api';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { useCreatePublicZippingTask } from '~/components/ZippingTasks/hooks/useCreatePublicZippingTask';
import { ZippingTasksPanel } from '~/components/ZippingTasks/ZippingTasksPanel';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeZippingTasksSelectorByShortId } from '~/store/tasks/selectors';
import { ReadyState } from '~/types/sockets';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePublicSyncTasks } from '~/utils/taskUtils/usePublicSyncTasks';

export const PublicZippingTasksPanel = memo(() => {
  const { workspaceId } = usePublicWorkspace();
  const { shortId } = useShortIdContext();
  const tasksSelector = useMemo(() => makeZippingTasksSelectorByShortId(shortId), [shortId]);
  const dispatch = useDispatch();
  const zippingTasks = useAirSelector(tasksSelector);
  const { cancelTask } = useCancelTask();
  const { createPublicZippingTask } = useCreatePublicZippingTask();

  const getDownloadUrl = useCallback(
    (taskId: string | undefined) => {
      if (!taskId) {
        throw new Error('No task id');
      }

      return ShortUrl.Tasks.ContentExporter.getDownloadUrl(shortId, { taskId });
    },
    [shortId],
  );

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePublicSyncTasks({
    workspaceId,
    shortId,
    tasksSelector,
    localType: 'ZippingForDownload',
    remoteType: 'ContentExporter',
    onComplete: async ({ task, enrichMetadata }) => {
      const { downloadUrl } = await getDownloadUrl(task.id);

      enrichMetadata({
        url: downloadUrl,
      });
    },
    onUpdate: async ({ task, enrichMetadata }) => {
      if (task.data?.secondsRemaining) {
        enrichMetadata({
          secondsRemaining: task.data.secondsRemaining,
        });
      }
    },
  });

  const syncExecutedForReadyStateRef = useRef<boolean>();

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  /**
   * Reset sync flag when task subscriptions become not ready
   */
  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  /**
   * On mount, take the tasks from local storage and add them to Redux
   */
  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  /**
   * Sync tasks when WebSocket reconnects to catch any missed completion events.
   * Only sync when task subscriptions are confirmed ready to avoid race conditions.
   */
  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  /**
   * Also sync when task subscriptions become ready after reconnection.
   */
  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  /**
   * When this panel unmounts (not possible in a public board but better safe than sorry),
   * reset the state
   */
  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: zippingTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  const handleRetryFailed = useCallback(
    (task: (typeof zippingTasks)[number]) => {
      if (task.status !== 'error') {
        return;
      }

      void createPublicZippingTask({
        boardIds: task.metadata.boardIds,
        clipIds: task.metadata.clipIds,
        collectionIds: task.metadata.collectionIds,
      });
      cancelTask(task.localTaskId);
    },
    [cancelTask, createPublicZippingTask],
  );

  if (zippingTasks.length < 1) {
    return null;
  }

  return (
    <ZippingTasksPanel
      onHandleExpiredLink={getDownloadUrl}
      onRetryFailed={handleRetryFailed}
      zippingTasks={zippingTasks}
      onCancel={cancelTask}
      onClear={cancelTask}
    />
  );
});

PublicZippingTasksPanel.displayName = 'PublicZippingTasksPanel';
